import React, { FC, useContext, useEffect, useState } from 'react';
import OrganizationCard from './organization-card';
import { Icons, Images } from '../../../../assets';
import { ButtonLoader } from '../../../../components';
import { AccountContext, AccountContextInterface } from '../../../../contexts';
import { AccountOrganization } from '../../../../models/organization.model';
import { Tab } from '../../../../types';

interface OrganizationSectionProps {
  accountId: string;
  onOrganizationClick: (organization: AccountOrganization, type: Tab.Creds | Tab.Reccos) => void;
}

const OrganizationSection: FC<OrganizationSectionProps> = ({ accountId, onOrganizationClick }) => {
  const { accountService } = useContext<AccountContextInterface>(AccountContext);
  const [organizations, setOrganizations] = useState<AccountOrganization[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { data } = await accountService.getAccountOrganizations(accountId, page, 5);
        if (data) {
          if (page === 1) {
            setOrganizations(data);
          } else {
            setOrganizations(prevOrganizations => [...prevOrganizations, ...data]);
          }
          if (data.length < 5) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    fetchData();
  }, [accountId, accountService, page]);

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      setPage(prevPage => prevPage + 1);
    }
  };

  return (
    <div className="bg-white flex flex-col gap-6 mb-4">
      <div className="flex gap-2 items-center">
        <img src={Icons.TIMELINE} alt="organizations" className="h-6 w-6" />
        <span className="font-semibold text-lg text-[#1A1A1A]">Timeline</span>
      </div>
      {loading && organizations.length === 0 && (
        <div className="flex items-center justify-center py-10">
          <ButtonLoader />
        </div>
      )}
      {!loading && organizations.length === 0 && (
        <div className="flex flex-col gap-4 items-center justify-center py-10">
          <img src={Images.NO_ACTIVITY} alt="No Timeline" className="h-32 w-32" />
          <p>No Timeline</p>
        </div>
      )}
      {organizations.length > 0 && (
        <div className="flex flex-col gap-6">
          <div className="gap-6 grid grid-cols-1">
            {organizations.map(organization => (
              <OrganizationCard key={organization.id} organization={organization} onOrganizationClick={onOrganizationClick} />
            ))}
          </div>
          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="bg-blue-500 px-4 py-2 rounded-lg text-white disabled:bg-gray-300">
              {loading ? <ButtonLoader /> : 'View More'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OrganizationSection;
